import type { BaseEntity } from '../base';
import type { DocumentId, Dictionary } from '../types';

type Node<TKey extends string> = { [x in TKey]?: Node<TKey>[] } & { parentNode?: Node<TKey> };

type Options<TKey extends string> = {
  nestedKey: TKey;
  connectParentNode?: boolean;
  lazy?: boolean;
};

export interface ListToTree<
  TList extends Dictionary<unknown>,
  TKey extends string,
  TNode extends Node<TKey>,
> {
  (rootIds: DocumentId[], list: TList): TNode[];
}

export const createListToTree = <
  TDoc extends BaseEntity,
  TList extends Dictionary<unknown>,
  TKey extends string,
  TNode extends Node<TKey>,
>(
  createNode: (item: TList[keyof TList & string]) => TNode | void,
  getDocument: (item: TList[keyof TList & string]) => TDoc | void,
  options: Options<TKey>,
): ListToTree<TList, TKey, TNode> => {
  const { connectParentNode = true, nestedKey, lazy = true }: Options<TKey> = options;

  return (rootIds: DocumentId[], list: TList) => {
    function createNodesTree(id: keyof TList & string) {
      const item = list[id];
      const document = getDocument(item);
      const node = createNode(item);

      if (!document || !node) return;

      const { childrenIds } = document.meta;
      if (childrenIds && childrenIds.length > 0 && !document.composite) {
        let children: TNode[] | undefined;

        let i = 0;
        while (i < childrenIds.length) {
          const childNode = createNodesTree(childrenIds[i++]);
          if (childNode) {
            if (connectParentNode) {
              childNode.parentNode = node;
            }

            children ??= [];
            children.push(childNode);
          }
        }

        // If there are no children, parent doesn't need to be created as well.
        if (children || lazy) {
          return {
            ...node,
            [nestedKey]: children,
          };
        }
      } else {
        return node;
      }
    }

    return rootIds.map(id => createNodesTree(id)).filter((node): node is TNode => !!node);
  };
};
